// Adapter: backend EventPublic -> the existing frontend event shape (Phase 10D).
//
// Principles:
//  * Never fabricate attendees, capacity, prices, or content — missing optional
//    values become null/'' and the UI hides the corresponding element.
//  * Normalize image paths to something the SPA can actually serve.

const PLACEHOLDER_IMAGE = '/assets/events/poster-soundwolves.jpg';
const BACKEND = process.env.REACT_APP_BACKEND_URL || '/api';

// Backend stores some legacy image paths (/images/.., /events/..) that do not
// exist under the SPA's public dir. Pass through http(s), /assets/, and DJ event
// poster references (/api/.. served paths or bare 32-hex poster keys); fall back
// to the generic placeholder for anything else (incl. null).
export function normalizeImage(src) {
  if (!src) return PLACEHOLDER_IMAGE;
  if (/^https?:\/\//i.test(src)) return src;
  if (src.startsWith('/api/')) return src;                       // DJ poster served path
  if (/^[a-f0-9]{32}\.(jpg|jpeg|png|webp)$/i.test(src)) return `${BACKEND}/events/poster/${src}`;
  if (src.startsWith('/assets/')) return src;
  return PLACEHOLDER_IMAGE;
}

// Combine a date (date-only for DJ events, or a full ISO datetime for catalog
// events) with an optional separate HH:MM time into one ISO string the date/time
// formatters can render. DJ events store start_date + start_time separately;
// catalog events embed the time in start_date already.
function combineDateTime(dateStr, timeStr) {
  if (!dateStr) return null;
  const s = String(dateStr);
  if (timeStr && /^\d{4}-\d{2}-\d{2}/.test(s) && !s.includes('T')) {
    return `${s.slice(0, 10)}T${String(timeStr).slice(0, 5)}:00Z`;
  }
  return s;
}

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  // Render the stored wall-clock time (backend stores intended-local time with a
  // 'Z' suffix), so pin to UTC — otherwise the viewer's timezone shifts it.
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

function fmtTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  // timeZone:'UTC' -> 21:00Z renders as 9:00 PM, 02:00Z as 2:00 AM (event-local).
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' });
}

const num = (v) => (typeof v === 'number' && !isNaN(v) ? v : null);

// Map one backend event to the canonical frontend shape the pages consume.
export function adaptEvent(e) {
  if (!e) return null;
  const priceNum = num(e.ticket_price);
  const vipNum = num(e.vip_price);
  const startISO = combineDateTime(e.start_date, e.start_time);
  const endISO = combineDateTime(e.end_date, e.end_time);
  // DJ events carry an explicit free/paid type; render "Free" or "Starts from $X".
  const isFree = e.event_type === 'free';
  const isPaid = e.event_type === 'paid';
  let priceLabel;
  if (isFree) priceLabel = 'Free';
  else if (priceNum != null) priceLabel = `$${priceNum}`;
  else priceLabel = 'TBA';
  // Poster gallery: normalize each stored key/path to a served URL.
  const posters = (Array.isArray(e.posters) ? e.posters : [])
    .map((p) => normalizeImage(p))
    .filter((u) => u && u !== PLACEHOLDER_IMAGE);
  const primaryImage = normalizeImage(e.poster_image || (e.posters && e.posters[0]));
  return {
    id: String(e.id),
    slug: e.slug,
    title: e.title || 'Untitled Event',
    artist: e.artist || '',
    description: e.description || '',
    venue: e.venue || '',
    city: e.city || '',
    address: e.address || e.city || '',
    state: e.state || '',
    zip: e.zip || '',
    dateISO: startISO,
    date: fmtDate(startISO),
    time: fmtTime(startISO),
    endTime: endISO ? fmtTime(endISO) : '',
    image: primaryImage,
    posters,                        // [] for catalog events; 1–3 served URLs for DJ events
    // Prices: show a real value or a neutral "TBA" — never a fabricated number.
    price: priceLabel,
    priceNum,                       // may be null (price filters guard on this)
    eventType: e.event_type || null,
    isFree,
    isPaid,
    vipPrice: vipNum != null ? `$${vipNum}` : 'TBA',
    vipPriceNum: vipNum,            // may be null
    category: e.category || '',
    tags: Array.isArray(e.tags) ? e.tags : [],
    highlights: Array.isArray(e.highlights) ? e.highlights : [],
    whatToExpect: Array.isArray(e.what_to_expect) ? e.what_to_expect : [],
    trending: !!e.trending,
    featured: !!e.featured,
    capacity: num(e.capacity),      // null -> UI hides the capacity indicator
    attendees: num(e.attendees),    // null -> UI hides the attendee count
    ageRestriction: e.age_restriction || '',
    friendsAttending: [],           // backend has no social data; never fabricate
    ticketLink: e.ticket_link || null,
    seriesId: e.series_id || null,
    occurrenceDate: e.occurrence_date || null,
    status: e.status || 'published',
  };
}

export function adaptEvents(list) {
  return Array.isArray(list) ? list.map(adaptEvent).filter(Boolean) : [];
}

// ---- Event identity helpers (My Events / Tickets resolution) ----------------
// Catalog events expose their identity under mixed fields: newer events use
// `id` (+ `event_id`/`slug`), while legacy seed events carry only `event_id`
// (with `id`/`slug` null). Saved user-event records store whichever identifier
// was current when saved. These helpers make lookups field-agnostic.

const _usableKey = (k) => {
  if (k == null) return null;
  const s = String(k);
  return (s === '' || s === 'undefined' || s === 'null') ? null : s;
};

// The most stable identifier for an event, as a string: event_id, then id,
// then _id, then slug. Returns null when none is usable.
export function getEventIdentifier(event) {
  if (!event) return null;
  for (const c of [event.event_id, event.id, event._id, event.slug]) {
    const k = _usableKey(c);
    if (k) return k;
  }
  return null;
}

// Build a lookup of adapted events keyed by EVERY identifier each catalog event
// exposes (event_id, id, _id, slug) plus the adapted id/slug — so a saved record
// resolves no matter which identifier field it stored. Never registers empty /
// 'undefined' / 'null' keys.
export function buildEventIndex(catalog) {
  const map = {};
  (Array.isArray(catalog) ? catalog : []).forEach((raw) => {
    const adapted = adaptEvent(raw);
    if (!adapted) return;
    for (const c of [raw?.event_id, raw?.id, raw?._id, raw?.slug, adapted.id, adapted.slug]) {
      const k = _usableKey(c);
      if (k) map[k] = adapted;
    }
  });
  return map;
}

// Resolve the adapted catalog event for a saved user-event record, trying the
// identifier fields a saved record may carry (event_id, id, nested event.*).
export function resolveSavedEvent(index, saved) {
  if (!index || !saved) return null;
  for (const c of [saved.event_id, saved.id, saved.event?.event_id, saved.event?.id]) {
    const k = _usableKey(c);
    if (k && index[k]) return index[k];
  }
  return null;
}
